import * as React from "react";
import { createDefaultProject, ProjectLineItem } from "@interfaces/project";
import { useOrganisation } from "@hooks/crud/useOrganisation";
import {
  createDefaultOrganisation,
  OrganisationLineItem,
} from "@interfaces/organisation";
import { createDefaultProfile, ProfileLineItem } from "@interfaces/profile";
import { useProject } from "@hooks/crud/useProject";
import { useParams } from "react-router-dom";
import { useProfileContext } from "./useProfileContext";
import { PageLoader } from "@stories/atoms/Loaders/PageLoader/PageLoader";
import { useContractorPackage } from "@hooks/crud/useContractorPackages";
import {
  ContractorPackageLineItem,
  createDefaultContractorPackage,
} from "@interfaces/contractorPackage";

type PC = {
  selectedProject: ProjectLineItem;
  selectedProjectOrganisation: OrganisationLineItem;
  contractorPackage: ContractorPackageLineItem;
  allProjects: ProjectLineItem[];
  error?: string;
  isOwner: boolean;
  isProjectLoading: boolean;
  userProfile: ProfileLineItem;
  setSelectedProject: (project: ProjectLineItem) => void;
  createProject: (project: ProjectLineItem) => Promise<void>;
  updateProject: (project: ProjectLineItem) => Promise<void>;
};

export const ProjectContext = React.createContext<PC>({
  selectedProject: createDefaultProject(),
  selectedProjectOrganisation: createDefaultOrganisation(),
  contractorPackage: createDefaultContractorPackage(createDefaultProject()),
  allProjects: [],
  isProjectLoading: false,
  isOwner: false,
  userProfile: createDefaultProfile(),
  setSelectedProject: () => {
    alert("No implemented");
  },
  createProject: () => Promise.resolve(),
  updateProject: () => Promise.resolve(),
});

type ProjectProviderProps = {
  children: React.ReactNode;
};

const ProjectProvider = (props: ProjectProviderProps) => {
  const { projectId } = useParams();
  const [contractorPackage, setContractorPackage] =
    React.useState<ContractorPackageLineItem>();
  if (!projectId) {
    throw Error("Project id is required");
  }

  const { userProfile } = useProfileContext();
  if (!userProfile) {
    throw Error("User profile is required");
  }

  const {
    isProjectLoading,
    selectedProject,
    projects,
    setSelectedProject,
    getProject,
    createProject,
    updateProject,
  } = useProject();

  const { isContractorPackageLoading, getContractorPackage } =
    useContractorPackage();

  const { projectOrganisation, getProjectOrganisation } = useOrganisation();

  React.useEffect(() => {
    const callGetProject = async (projectId: string) => {
      if (projectId) {
        await Promise.all([
          getProject(projectId),
          getProjectOrganisation(projectId),
        ]);
        const selectedPackage = await getContractorPackage(projectId);
        setContractorPackage(selectedPackage);
      }
    };
    if (projectId) {
      callGetProject(projectId);
    }
  }, [projectId]);

  if (
    isProjectLoading ||
    isContractorPackageLoading ||
    !contractorPackage ||
    !selectedProject ||
    !projectOrganisation
  ) {
    return <PageLoader />;
  }

  return (
    <ProjectContext.Provider
      value={{
        selectedProject,
        selectedProjectOrganisation: projectOrganisation,
        contractorPackage,
        allProjects: projects,
        isProjectLoading,
        userProfile,
        isOwner: selectedProject.organisationId === userProfile.organisationId,
        setSelectedProject,
        createProject,
        updateProject,
      }}
    >
      {props.children}
    </ProjectContext.Provider>
  );
};

const useProjectContext = () => React.useContext(ProjectContext);

export { ProjectProvider, useProjectContext };
